// Client referral/promo helpers.
//
// - On app load call `capturePendingReferral()` to pull `?ref=VODA-XXXX` out
//   of the URL and stash it in localStorage.
// - After sign-in AuthService calls `redeemPendingReferral(user)` which
//   posts to `redeemReferral` and clears the stash on success (or on a
//   terminal error like SELF_REFERRAL / CODE_NOT_FOUND — otherwise we keep
//   the code around so a temporary failure doesn't destroy it).
// - `redeemPromo(code)` is used by the Invite page's promo form.

import { registerPlugin } from '@capacitor/core';
import { auth, analytics, logEvent } from '../firebase.js';
import { getPlatform } from './platform-service.js';

const FUNCTIONS_BASE = 'https://us-central1-voda-7647c.cloudfunctions.net';
const PENDING_KEY = 'voda_pending_ref';
const INSTALL_REF_KEY = 'archelier_install_ref_checked';

async function authedFetch(path, body) {
  const user = auth.currentUser;
  if (!user || user.isAnonymous) {
    throw new Error('SIGN_IN_REQUIRED');
  }
  const token = await user.getIdToken();
  const res = await fetch(`${FUNCTIONS_BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body || {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(json.error || 'REQUEST_FAILED');
    err.code = json.error;
    err.status = res.status;
    throw err;
  }
  return json;
}

// 새 코드는 ARCH-, 예전 코드는 VODA- (역인덱스를 안 지워서 옛 링크도 유효).
// 서버 functions/referral.js 의 CODE_RE 와 같아야 한다.
export const REFERRAL_CODE_RE = /^(ARCH|VODA)-[A-Z0-9]{4}$/;

function normalize(code) {
  return String(code || '').trim().toUpperCase();
}

export const ReferralService = {
  /**
   * Read ?ref= from the current URL and stash it in localStorage so we can
   * redeem after the next Google sign-in. Call this at app boot. Noop if
   * the URL has no ref param.
   *
   * Cleans the query string so the code doesn't stick around in the address
   * bar or leak into analytics referrer fields.
   */
  capturePendingReferral() {
    try {
      const params = new URLSearchParams(window.location.search);
      const code = this.stashFromParams(params);
      if (!code) return;

      params.delete('ref');
      const next = params.toString();
      const url = window.location.pathname + (next ? `?${next}` : '') + window.location.hash;
      window.history.replaceState({}, '', url);
    } catch (e) {
      console.warn('capturePendingReferral failed:', e);
    }
  },

  /**
   * 네이티브 딥링크(appUrlOpen)용. capturePendingReferral 은 main.jsx 에서
   * **부팅 때 한 번만** 돌기 때문에, 앱이 이미 떠 있는 상태로 초대 링크가
   * 들어오면 아무도 ?ref= 를 읽지 않는다. NativeUrlHandler 가 이걸 부른다.
   */
  capturePendingReferralFromUrl(urlString) {
    try {
      return this.stashFromParams(new URL(urlString).searchParams);
    } catch {
      return null;
    }
  },

  /** ?ref= 를 검증해서 stash. 성공하면 코드를, 아니면 null. */
  stashFromParams(params) {
    const raw = params.get('ref');
    if (!raw) return null;
    const code = normalize(raw);
    if (!REFERRAL_CODE_RE.test(code)) return null;
    localStorage.setItem(PENDING_KEY, code);
    // 유입 측정 — 주소창에서 ?ref= 를 지우기 때문에 GA4 의 pageLocation 에는
    // 흔적이 남지 않는다 (실제로 90일간 0건이었다). 성사(referredBy) 만 보면
    // "아무도 안 눌렀는지" 와 "누르고 이탈했는지" 를 구분할 수 없다.
    try { logEvent(analytics, 'referral_link_opened', { code }); } catch { /* 무시 */ }
    return code;
  },

  /**
   * Android 앱 첫 실행 때 한 번 — Play 설치 리퍼러에서 초대 코드를 꺼내 stash.
   * 랜딩의 Google Play 버튼이 `&referrer=ref%3DARCH-XXXX` 를 달고 간다 (app-links.js
   * playStoreUrl). 브라우저 localStorage 는 앱으로 안 넘어오지만 이 값은 Play 가 넘겨준다.
   * iOS 엔 이런 통로가 없다 — 앱을 깐 뒤 초대 링크를 다시 누르면 유니버설 링크로 들어온다.
   */
  async captureInstallReferrer() {
    if (getPlatform() !== 'android') return null;
    try {
      if (localStorage.getItem(INSTALL_REF_KEY)) return null;
      const { referrer } = await registerPlugin('InstallReferrer').get();
      localStorage.setItem(INSTALL_REF_KEY, '1');
      return this.stashFromParams(new URLSearchParams(referrer || ''));
    } catch (e) {
      console.warn('captureInstallReferrer failed:', e);
      return null;
    }
  },

  getPendingReferral() {
    try { return localStorage.getItem(PENDING_KEY) || null; } catch { return null; }
  },

  clearPendingReferral() {
    try { localStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
  },

  /**
   * Called right after sign-in. If a pending referral code is stashed, try
   * to redeem it. Returns the server response on success, or null if
   * nothing to do / non-terminal failure.
   */
  async redeemPendingReferral() {
    const code = this.getPendingReferral();
    if (!code) return null;
    try {
      const result = await authedFetch('/redeemReferral', { code });
      this.clearPendingReferral();
      return result;
    } catch (err) {
      // Terminal errors — the code will never work for this user, so drop it.
      if (['SELF_REFERRAL', 'CODE_NOT_FOUND', 'INVALID_CODE'].includes(err.code)) {
        this.clearPendingReferral();
      }
      console.warn('redeemPendingReferral failed:', err.code || err.message);
      return null;
    }
  },

  /**
   * 초대 코드를 손으로 입력해서 사용 (Invite 페이지).
   *
   * 왜 필요한가: 초대 링크를 브라우저에서 열고 거기서 앱을 설치하면, 코드를
   * 담아둔 브라우저 localStorage 가 앱으로 넘어오지 않는다 (deferred deep
   * linking 이 없으면 iOS/Android 모두 방법이 없다). 그러면 보상이 조용히
   * 사라진다. redeemReferral 은 가입 시점 전용이 아니라 `referredBy` 가 비어
   * 있으면 언제든 되므로, 설치 후에 직접 넣는 경로를 열어 두면 구멍이 막힌다.
   * Throws with err.code = server error string on failure.
   */
  async redeemCode(code) {
    const normalized = normalize(code);
    if (!REFERRAL_CODE_RE.test(normalized)) {
      const e = new Error('INVALID_CODE'); e.code = 'INVALID_CODE'; throw e;
    }
    const result = await authedFetch('/redeemReferral', { code: normalized });
    // 로컬에 대기 중이던 같은 코드가 있으면 정리 — 안 그러면 다음 로그인 때
    // 다시 시도해서 ALREADY 응답만 받는다.
    if (this.getPendingReferral() === normalized) this.clearPendingReferral();
    return result;
  },

  /**
   * Manual promo code redemption (Invite page).
   * Throws with err.code = server error string on failure.
   */
  async redeemPromo(code) {
    const normalized = normalize(code);
    if (!normalized) {
      const e = new Error('INVALID_CODE'); e.code = 'INVALID_CODE'; throw e;
    }
    return authedFetch('/redeemPromo', { code: normalized });
  },
};
